import axios, { AxiosResponse } from 'axios';
import { Mutex } from 'async-mutex';
import Config from './config';

import { Heartbeat } from '../../models/dataModels';
import {
  UserPreferences,
  ShiftLog,
  TakeOverDetails,
  ReportTemplate,
  UUID,
  APIHierarchy,
  CreateShiftLogResponse,
  ShiftLogDetails,
  ShiftLogStatusChangeResponse,
  StandingOrderActionResponse,
  TaskActionResponse,
  DateTime,
} from './dataModels';
import { aquireToken } from './auth/Auth';
import { apiScope, emkitScope } from './auth/msalConfig';
import { DdLogs } from '../logging/datadog';
import axiosRetry from 'axios-retry';

const lock = new Mutex();

const requestLoggerPrefix = 'REQUEST INTERCEPTOR:'
const responseLoggerPrefix = 'RESPONSE INTERCEPTOR:'
const retryLoggerPrefix = 'RETRY INTERCEPTOR:'
const defaultTimeout = 120000;

function newAbortSignal(timeoutMs) {
  const abortController = new AbortController();
  setTimeout(() => {
    abortController.abort()
  }, timeoutMs || defaultTimeout);

  return abortController.signal
}

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  shouldResetTimeout: true,
  retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
  onRetry: (retryCount, error, requestConfig) => {
    DdLogs.debug(`${retryLoggerPrefix} Attempt: ${retryCount} for ${requestConfig.url} - ${error.message}`);
  },
  onMaxRetryTimesExceeded: (error, retryCount) => {
    DdLogs.error(`${retryLoggerPrefix} Failed to successfully call api after ${retryCount} attempts - ${error.message}`);
  }
});

axios.interceptors.response.use(
  (response) => {
    DdLogs.info(`${responseLoggerPrefix} sucessfully received ${response.status} status from ${response.config.url}`);

    if (Config.LOG_RESPONSES.toLowerCase() === 'true') {
      // for some reason, longer responses get truncated
      // Full response body — not logged to keep Datadog cost down.
      // DdLogs.info(`${responseLoggerPrefix} response: \n ${JSON.stringify(response.data, null, 2)}`);
    }
    return response;
  },
  (error) => {
    // https://stackoverflow.com/a/51768316
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      DdLogs.error(`${responseLoggerPrefix} error ${error.response.status} status from ${error.response.config.method} method for ${error.response.config.url} with data: ${JSON.stringify(error.response.data)} and headers: ${JSON.stringify(error.response.headers)}`);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser 
      // and an instance of http.ClientRequest in node.js
      DdLogs.error(`${responseLoggerPrefix} error no response from ${error.config?.method ?? 'unknown'} method for ${error.config?.url ?? 'unknown'}`, {
        error: { name: error.name, message: error.message, stack: error.stack, code: error.code },
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      DdLogs.error(`${responseLoggerPrefix} - unexpected error: ${error?.message ?? String(error)}`, {
        error: { name: error?.name, message: error?.message, stack: error?.stack, code: error?.code },
      });
    }
    return Promise.reject(error);
  }
);

axios.interceptors.request.use(
  async (config) => {
    let url: URL;
    let scope: string;

    if (config.url?.startsWith('api://')) {
      url = new URL(`${Config.API_URL}/${config.url.slice('api://'.length)}`);
      scope = apiScope;
    } else if (config.url?.startsWith('emkit://')) {
      url = new URL(`https://core.mobility.xom.cloud/api/${config.url.slice('emkit://'.length)}`);
      scope = emkitScope;
    } else {
      throw new Error(`Invalid url: ${config.url}`);
    }

    DdLogs.info(`${requestLoggerPrefix} Calling ${config.method} method for ${url} with scope: ${scope}`);
    if (config.data) {
      // Request body — not logged to keep Datadog cost down.
      // DdLogs.info(`${requestLoggerPrefix} Sending data: ${JSON.stringify(config.data)}`);
    }

    config.baseURL = undefined;
    config.url = url.toJSON();
    
    let response: { accessToken: string };
    await lock.runExclusive(async () => {
      response = await aquireToken(scope);
    });

    if (!config.timeout && !config.signal) {
      DdLogs.info(`${requestLoggerPrefix} Response timeout not set, updating to ${defaultTimeout}ms`);
      config.signal = newAbortSignal(defaultTimeout);
    }

    DdLogs.info(`${requestLoggerPrefix} ${!response.accessToken ? 'Un' : ''}sucessfully fetched access token for ${scope}`);

    if (response.accessToken) {
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    }

    return config;
  },
  (error) => {
    DdLogs.error(`${requestLoggerPrefix} error, ${error}`);
    return Promise.reject(error);
  },
);

interface ShiftLogsParameters {
  siteCode: string;
  showType?: string; //not defined by API doc
  assetName?: string;
  shiftSummaryID?: UUID;
  lastUpdate?: string;
  fileDownload?: boolean;
}

const apiClient = {
  heartBeat(): Promise<AxiosResponse<Heartbeat>> {
    return axios.get('api://heartbeat');
  },

  getAssetHierarchy(): Promise<AxiosResponse<APIHierarchy>> {
    return axios.get('api://hierarchies');
  },

  getUserPreferences(siteCode: string): Promise<AxiosResponse<UserPreferences>> {
    return axios.get(`api://preferences/${siteCode}`);
  },

  setUserPreferences(siteCode: string, assetName: string): Promise<AxiosResponse<UserPreferences>> {
    return axios.put(`api://preferences/${siteCode}`, {
      assetName,
    });
  },

  getShiftLogs({
    siteCode,
    showType,
    assetName,
    shiftSummaryID,
    lastUpdate,
    fileDownload,
  }: ShiftLogsParameters): Promise<AxiosResponse<ShiftLog[]>> {
    return axios.get(`api://shiftlogs/${siteCode}/summaries`, {
      params: {
        numDays: 2,
        show: showType,
        assetName: assetName,
        shiftSummaryID: shiftSummaryID,
        lastUpdate: lastUpdate,
        fileDownload: fileDownload,
      },
    });
  },

  getTakeOverDetails(
    siteCode: string,
    shiftSummaryID: UUID,
  ): Promise<AxiosResponse<TakeOverDetails>> {
    return axios.get(`api://shiftlogs/${siteCode}/takeoverDetails/${shiftSummaryID}`);
  },

  takeOverShiftLog(
    siteCode: string,
    shiftSummaryID: UUID,
    takeoverComments: string,
  ): Promise<AxiosResponse<ShiftLogStatusChangeResponse>> {
    return axios.put(`api://shiftlogs/${siteCode}/${shiftSummaryID}`, {
      action: 'takeover',
      takeoverComments: takeoverComments,
    });
  },

  submitShiftLog(siteCode: string, shiftSummaryID: UUID): Promise<AxiosResponse<ShiftLog>> {
    return axios.put(`api://shiftlogs/${siteCode}/${shiftSummaryID}`, {
      action: 'submit',
      takeoverComments: '',
    });
  },

  unsubmitShiftLog(siteCode: string, shiftSummaryID: UUID): Promise<AxiosResponse<ShiftLog>> {
    return axios.put(`api://shiftlogs/${siteCode}/${shiftSummaryID}`, {
      action: 'unsubmit',
      takeoverComments: '',
    });
  },

  getReportTemplates(
    siteCode: string,
    assetName: string,
  ): Promise<AxiosResponse<ReportTemplate[]>> {
    return axios.get(`api://shiftlogs/${siteCode}/templates`, {
      params: {
        assetName: assetName,
      },
    });
  },

  createShiftLog(
    siteCode: string,
    assetName: string,
    templateId: string,
  ): Promise<AxiosResponse<CreateShiftLogResponse>> {
    return axios.post(`api://shiftlogs/${siteCode}/summaries`, null, {
      params: {
        assetName: assetName,
        templateName: templateId,
      },
    });
  },

  updateShiftLog(
    siteCode: string,
    assetName: string,
    shiftLogDetails: ShiftLogDetails,
  ): Promise<AxiosResponse<ShiftLogDetails>> {
    return axios.put(`api://shiftlogs/${siteCode}/logdetails?assetName=${assetName}`, shiftLogDetails);
  },

  getShiftLogDetails(
    siteCode: string,
    shiftSummaryID: UUID,
  ): Promise<AxiosResponse<ShiftLogDetails>> {
    return axios.get(`api://shiftlogs/${siteCode}/logdetails/${shiftSummaryID}`);
  },

  activateStandingOrder(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    standingOrderId: UUID,
  ): Promise<AxiosResponse<StandingOrderActionResponse>> {
    return axios.put(`api://standingorder/${siteCode}/activate`, {
      displayTemplateId: displayTemplateId,
      standingOrderId: standingOrderId,
      assetName: assetName,
    });
  },

  completeStandingOrder(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    standingOrderId: UUID,
  ): Promise<AxiosResponse<StandingOrderActionResponse>> {
    return axios.put(`api://standingorder/${siteCode}/complete`, {
      displayTemplateId: displayTemplateId,
      standingOrderId: standingOrderId,
      assetName: assetName,
    });
  },

  postStandingOrderComment(
    siteCode: string,
    standingOrderId: UUID,
    comment: string,
  ): Promise<AxiosResponse<never>> {
    return axios.post(`api://standingorder/${siteCode}/comment`, {
      standingOrderId: standingOrderId,
      comment: comment,
    });
  },

  startTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    startTime: DateTime,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/start`, {
      assetName,
      displayTemplateId,
      taskId,
      startTime,
    });
  },

  pauseTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    comment?: string,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/pause`, {
      assetName,
      displayTemplateId,
      taskId,
      comment,
    });
  },

  resumeTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    comment?: string,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/resume`, {
      assetName,
      displayTemplateId,
      taskId,
      comment,
    });
  },

  completeTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    startTime: DateTime,
    endTime: DateTime,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/complete`, {
      assetName,
      displayTemplateId,
      taskId,
      startTime,
      endTime,
    });
  },

  cancelTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    comment?: string,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/cancel`, {
      displayTemplateId,
      taskId,
      assetName,
      comment,
    });
  },

  updatePercentage(
    siteCode: string,
    displayTemplateId: string,
    assetName: string,
    taskId: UUID,
    percentCompleted: number,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return axios.put(`api://task/${siteCode}/percent`, {
      displayTemplateId,
      taskId,
      assetName,
      percentCompleted,
    });
  },

  postTaskComment(
    siteCode: string,
    assetName: string,
    taskId: UUID,
    comment: string,
  ): Promise<AxiosResponse<never>> {
    return axios.post(`api://task/${siteCode}/comment`, {
      taskId: taskId,
      assetName: assetName,
      comment: comment,
    });
  },
};

export default apiClient;
export type ApiClient = typeof apiClient;
